<script setup>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import ReportsContextBar from './ReportsContextBar.vue';
import { useReportsContext } from '../composables/useReportsContext';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountScopedRoute } = useAccount();

// Nav em 2 niveis (modelo do CRM): Conversas e o guarda-chuva do
// atendimento — as dimensoes (Agentes/Caixas/Equipes/Etiquetas), a
// jornada (Disponibilidade) e o Bot sao recortes do MESMO dado e viram
// abas dentro dele, em vez de poluir o menu principal.
const MAIN_NAV = [
  {
    label: 'Dashboard ao Vivo',
    routeName: 'account_overview_reports',
  },
  {
    label: 'Conversas',
    routeName: 'conversation_reports',
    group: 'conversas',
  },
  {
    label: 'Brain',
    routeName: 'bot_reports',
  },
  {
    label: 'CRM',
    routeName: 'crm_reports',
  },
  {
    label: 'Campanhas',
    routeName: 'campaigns_reports',
  },
  {
    label: 'Landing Pages',
    routeName: 'landing_pages_reports',
  },
  // Marketing = fonte de dados por plataforma, fora do CRM (o cruzamento
  // com vendas mora em CRM > Atribuicao).
  {
    label: 'Marketing',
    routeName: 'marketing_reports',
  },
];

const CONVERSAS_TABS = [
  {
    label: 'Visão Geral',
    routeName: 'conversation_reports',
    icon: 'i-lucide-layout-grid',
  },
  { label: 'Agentes', routeName: 'agent_reports_index', icon: 'i-lucide-user' },
  {
    label: 'Disponibilidade',
    routeName: 'agent_availability_reports',
    icon: 'i-lucide-clock',
  },
  {
    label: 'Caixas de Entrada',
    routeName: 'inbox_reports_index',
    icon: 'i-lucide-inbox',
  },
  { label: 'Equipes', routeName: 'team_reports_index', icon: 'i-lucide-users' },
  {
    label: 'Etiquetas',
    routeName: 'label_reports_index',
    icon: 'i-lucide-tags',
  },
  { label: 'CSAT', routeName: 'csat_reports', icon: 'i-lucide-smile' },
  { label: 'SLA', routeName: 'sla_reports', icon: 'i-lucide-timer' },
];

const CONVERSAS_ROUTES = new Set([
  'conversation_reports',
  'agent_reports_index',
  'agent_reports_show',
  'agent_availability_reports',
  'agent_availability_show',
  'inbox_reports_index',
  'inbox_reports_show',
  'team_reports_index',
  'team_reports_show',
  'label_reports_index',
  'label_reports_show',
  'csat_reports',
  'sla_reports',
]);

const inConversas = computed(() => CONVERSAS_ROUTES.has(route.name));

// Check if an item or its detail page is active
const isActive = routeName => {
  const current = route.name;
  if (current === routeName) return true;
  // Detalhe do vendedor pertence ao CRM
  if (current === 'crm_reports_agent' && routeName === 'crm_reports') {
    return true;
  }
  // Match detail pages (e.g. agent_reports_show matches agent_reports_index)
  if (current?.endsWith('_show') && routeName?.endsWith('_index')) {
    return current.replace('_show', '_index') === routeName;
  }
  // Match availability detail to availability reports
  if (
    current === 'agent_availability_show' &&
    routeName === 'agent_availability_reports'
  ) {
    return true;
  }
  // Match bot assistant detail to bot reports
  if (current === 'bot_reports_show' && routeName === 'bot_reports') {
    return true;
  }
  return false;
};

const isMainActive = item =>
  item.group === 'conversas' ? inConversas.value : isActive(item.routeName);

const ALL_REPORT_ROUTES = new Set([
  ...MAIN_NAV.map(i => i.routeName),
  ...CONVERSAS_ROUTES,
]);
const showNav = computed(
  () =>
    ALL_REPORT_ROUTES.has(route.name) ||
    route.name?.endsWith('_show') ||
    route.name === 'crm_reports_agent'
);

// Altura da pilha sticky: nav 40px (+ subnav 32px no guarda-chuva)
const contextStickyTop = computed(() => (inConversas.value ? 82 : 40));

// Barra de Contexto global — spec por rota (padrao ctxSpec do prototipo).
// O CRM registra a propria spec dinamicamente (muda por aba interna) via
// setPageSpec; o wrapper usa como fallback quando a rota nao esta aqui.
const { pageSpec } = useReportsContext();
const CTX_SPEC = {
  account_overview_reports: {
    realtime: true,
    period: true,
    scopes: ['team', 'inbox'],
  },
  conversation_reports: { period: true, businessHours: true, groupBy: true },
  agent_reports_index: { period: true, businessHours: true, scopes: ['agent'] },
  agent_reports_show: {
    period: true,
    businessHours: true,
    groupBy: true,
    scopes: ['agent'],
  },
  crm_reports_agent: { period: true },
  agent_availability_reports: { period: true, scopes: ['agent'] },
  agent_availability_show: { period: true, scopes: ['agent'] },
  inbox_reports_index: { period: true, businessHours: true, scopes: ['inbox'] },
  inbox_reports_show: {
    period: true,
    businessHours: true,
    groupBy: true,
    scopes: ['inbox'],
  },
  team_reports_index: { period: true, businessHours: true, scopes: ['team'] },
  team_reports_show: {
    period: true,
    businessHours: true,
    groupBy: true,
    scopes: ['team'],
  },
  label_reports_index: { period: true, businessHours: true, scopes: ['label'] },
  label_reports_show: {
    period: true,
    businessHours: true,
    groupBy: true,
    scopes: ['label'],
  },
  csat_reports: { period: true, scopes: ['agent', 'team', 'inbox', 'rating'] },
  sla_reports: {
    period: true,
    scopes: ['sla_policy', 'agent', 'inbox', 'team', 'label'],
  },
  bot_reports: { period: true, groupBy: true },
  bot_reports_show: { period: true },
  campaigns_reports: { period: true },
  landing_pages_reports: { period: true },
};
const contextSpec = computed(
  () => CTX_SPEC[route.name] || pageSpec.value || null
);

// ── Escopo de entidade <-> rota (drill-down) ──
// Nas telas de Agentes/Caixas/Equipes/Etiquetas o proprio drill-down e o
// escopo: escolher no chip navega pro detalhe, limpar volta pro indice,
// e entrar no detalhe (por clique na tabela) preenche o chip.
const { state: ctxState, setScope } = useReportsContext();
const ENTITY_PAIRS = [
  {
    key: 'agent',
    index: 'agent_reports_index',
    show: 'agent_reports_show',
    getter: 'agents/getAgents',
  },
  {
    key: 'inbox',
    index: 'inbox_reports_index',
    show: 'inbox_reports_show',
    getter: 'inboxes/getInboxes',
  },
  {
    key: 'team',
    index: 'team_reports_index',
    show: 'team_reports_show',
    getter: 'teams/getTeams',
  },
  {
    key: 'label',
    index: 'label_reports_index',
    show: 'label_reports_show',
    getter: 'labels/getLabels',
  },
  {
    key: 'agent',
    index: 'agent_availability_reports',
    show: 'agent_availability_show',
    getter: 'agents/getAgents',
  },
];

const currentPair = computed(() =>
  ENTITY_PAIRS.find(p => p.index === route.name || p.show === route.name)
);

const entityName = (pair, id) => {
  const items = store.getters[pair.getter] || [];
  const found = items.find(i => i.id === id);
  return found?.name || found?.title || `#${id}`;
};

// Rota -> escopo: detalhe preenche o chip, indice limpa
watch(
  () => [route.name, route.params.id],
  () => {
    const pair = currentPair.value;
    if (!pair) return;
    if (route.name === pair.show) {
      const id = Number(route.params.id);
      if (ctxState.scopes[pair.key]?.id !== id) {
        setScope(pair.key, { id, name: entityName(pair, id) });
      }
    } else if (ctxState.scopes[pair.key]) {
      setScope(pair.key, null);
    }
  },
  { immediate: true }
);

// Escopo -> rota: chip escolhido navega pro detalhe, x volta pro indice
watch(
  () => currentPair.value && ctxState.scopes[currentPair.value.key],
  scope => {
    const pair = currentPair.value;
    if (!pair) return;
    if (scope && route.name === pair.index) {
      router.push(accountScopedRoute(pair.show, { id: scope.id }));
    } else if (
      scope &&
      route.name === pair.show &&
      Number(route.params.id) !== scope.id
    ) {
      router.push(accountScopedRoute(pair.show, { id: scope.id }));
    } else if (!scope && route.name === pair.show) {
      router.push(accountScopedRoute(pair.index));
    }
  }
);
</script>

<template>
  <div class="reports--wrapper overflow-auto bg-n-surface-1 w-full">
    <!-- Nav principal (IDV): texto + sublinhado gradiente rose -->
    <nav
      v-if="showNav"
      class="reports-nav sticky top-0 z-10 flex items-center justify-center gap-0.5 px-4 border-b border-n-weak bg-n-solid-2 overflow-x-auto overflow-y-hidden whitespace-nowrap"
    >
      <RouterLink
        v-for="item in MAIN_NAV"
        :key="item.routeName"
        :to="accountScopedRoute(item.routeName)"
        class="relative px-3 pt-3 pb-[11px] text-[12.5px] leading-4 transition-colors whitespace-nowrap"
        :class="
          isMainActive(item)
            ? `text-n-slate-12 font-semibold after:absolute after:content-[''] after:left-2.5 after:right-2.5 after:bottom-0 after:h-[2.5px] after:rounded-t-[3px] after:bg-gradient-to-r after:from-[#9B2C2C] after:to-[#BE185D]`
            : 'text-n-slate-10 font-medium hover:text-n-slate-12'
        "
      >
        {{ item.label }}
      </RouterLink>
    </nav>

    <!-- Sub-nav do guarda-chuva Conversas — MESMO padrao nivel-1 do CRM
         (underline + icone), pedido do usuario 2026-09-03 -->
    <nav
      v-if="showNav && inConversas"
      class="sticky top-[40px] z-[9] flex items-center justify-center gap-1 px-4 border-b border-n-weak bg-n-solid-2 overflow-x-auto overflow-y-hidden no-scrollbar whitespace-nowrap"
    >
      <RouterLink
        v-for="tab in CONVERSAS_TABS"
        :key="tab.routeName"
        :to="accountScopedRoute(tab.routeName)"
        class="flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 border-b-2 -mb-px"
        :class="
          isActive(tab.routeName)
            ? 'text-n-slate-12 border-[#8B2520] dark:border-[#c4a882]'
            : 'text-n-slate-10 border-transparent hover:text-n-slate-12'
        "
      >
        <span :class="tab.icon" class="size-4" />
        {{ tab.label }}
      </RouterLink>
    </nav>

    <ReportsContextBar
      v-if="contextSpec"
      :spec="contextSpec"
      :sticky-top="contextStickyTop"
    />

    <div class="max-w-7xl mx-auto pb-12 px-6">
      <router-view />
    </div>
  </div>
</template>

<style scoped lang="scss">
.reports--wrapper {
  :deep() {
    .multiselect--disabled {
      @apply opacity-50 border border-n-weak rounded-md cursor-not-allowed;
    }

    .multiselect__content-wrapper {
      @apply bg-n-solid-2 border border-n-weak text-n-slate-12;
    }

    .multiselect__tags {
      @apply bg-n-slate-1 border border-n-weak m-0 min-h-[2.875rem] pt-0;

      input[type='text'] {
        @apply bg-n-alpha-3 border-n-weak placeholder:text-n-slate-11 min-h-[2.375rem] h-[2.375rem] px-0.5 py-0 text-sm;
      }
    }

    .multiselect__placeholder {
      @apply text-n-slate-11;
    }

    .multiselect__select {
      @apply min-h-0;
    }

    .multiselect__single {
      @apply bg-n-alpha-3 text-n-slate-11;
    }

    .multiselect__input {
      @apply text-sm h-[2.375rem] mb-0 py-0;
    }

    .multiselect__tags,
    .multiselect__input,
    .multiselect {
      @apply bg-n-alpha-3 !border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
    }

    .mx-input-wrapper {
      @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm;

      input {
        @apply border-n-weak text-sm;
      }
    }

    .multiselect__option {
      @apply flex items-center;
    }

    .mx-datepicker {
      .mx-input {
        background-color: transparent !important;
      }

      .mx-input-wrapper input::placeholder {
        @apply text-n-slate-11;
      }

      .mx-input-wrapper input {
        @apply text-n-slate-11;
      }
    }

    .multiselect--active:not(.multiselect--above) .multiselect__current,
    .multiselect--active:not(.multiselect--above) .multiselect__input,
    .multiselect--active:not(.multiselect--above) .multiselect__tags {
      @apply rounded-b-none;
    }
  }
}
</style>
